import { LiveData, Service } from '@toeverything/infra';

import type { WorkspacePropertiesAdapter } from './adapter';
import type { AugmentationStatus, WorkspaceAffinePageProperties } from './schema';

export class AugmentationStatusService extends Service {
  private readonly regenerationSourcePages$ = new LiveData<
    Readonly<Record<string, true>>
  >({});
  private readonly augmentedRenderPages$ = new LiveData<
    Readonly<Record<string, true>>
  >({});

  constructor(private readonly adapter: WorkspacePropertiesAdapter) {
    super();
  }

  regenerationSourceActive$(pageId: string): LiveData<boolean> {
    return this.regenerationSourcePages$.map(pages => pages[pageId] === true);
  }

  augmentedRenderActive$(pageId: string): LiveData<boolean> {
    return this.augmentedRenderPages$.map(pages => pages[pageId] === true);
  }

  isRegenerationSourceActive(pageId: string): boolean {
    return this.regenerationSourcePages$.value[pageId] === true;
  }

  isAugmentedRenderActive(pageId: string): boolean {
    return this.augmentedRenderPages$.value[pageId] === true;
  }

  beginRegenerationSourceRender(pageId: string): void {
    const pages = this.regenerationSourcePages$.value;
    if (pages[pageId]) return;
    this.regenerationSourcePages$.next({ ...pages, [pageId]: true });
  }

  endRegenerationSourceRender(pageId: string): void {
    const pages = this.regenerationSourcePages$.value;
    if (!pages[pageId]) return;
    const next = { ...pages };
    delete next[pageId];
    this.regenerationSourcePages$.next(next);
  }

  beginAugmentedRender(pageId: string): void {
    const pages = this.augmentedRenderPages$.value;
    if (pages[pageId]) return;
    this.augmentedRenderPages$.next({ ...pages, [pageId]: true });
  }

  endAugmentedRender(pageId: string): void {
    const pages = this.augmentedRenderPages$.value;
    if (!pages[pageId]) return;
    const next = { ...pages };
    delete next[pageId];
    this.augmentedRenderPages$.next(next);
  }

  status$(pageId: string): LiveData<AugmentationStatus> {
    return this.adapter.properties$.map(props => {
      const pageProps = props.pageProperties?.[pageId] as
        | WorkspaceAffinePageProperties
        | undefined;

      const value = pageProps?.system?.augmentationStatus?.value;
      return this.normalizeStatus(value);
    });
  }

  get statuses$(): LiveData<Record<string, AugmentationStatus>> {
    return this.adapter.properties$.map(props => {
      const entries = Object.entries(
        (props.pageProperties ?? {}) as Record<string, WorkspaceAffinePageProperties>
      );

      const result: Record<string, AugmentationStatus> = {};
      for (const [pageId, p] of entries) {
        const status = this.normalizeStatus(p.system?.augmentationStatus?.value);
        result[pageId] = status;
      }
      return result;
    });
  }

  getStatus(pageId: string): AugmentationStatus {
    return this.adapter.getPageAugmentationStatus(pageId);
  }

  setStatus(pageId: string, status: AugmentationStatus): void {
    this.adapter.setPageAugmentationStatus(pageId, status);
  }

  markOriginal(pageId: string): void {
    this.setStatus(pageId, 'Original');
  }

  markAugmented(pageId: string): void {
    this.setStatus(pageId, 'Augmented');
  }

  reset(pageId: string): void {
    this.setStatus(pageId, 'NotStarted');
  }

  private normalizeStatus(value: unknown): AugmentationStatus {
    return value === 'Original' || value === 'Augmented' || value === 'NotStarted'
      ? value
      : 'NotStarted';
  }
}
